import React from 'react';

import CostPerMileInfo from '../cost-per-mile-info/cost-per-mile-info';
import MilesDrivenAnnuallyInfo from '../miles-driven-annually-info/miles-driven-annually-info';
import TireLifeEstimateInfo from '../tire-life-estimate-info/tire-life-estimate-info';
import TireLifeSpanInfo from '../tire-life-span-info/tire-life-span-info';
import TireLifeWarrantyInfo from '../tire-life-warranty-info/tire-life-warranty-info';
import TreadwellBarRow, {
  TreadwellBarRowColor
} from '../treadwell-bar-row/treadwell-bar-row';

import {
  useAemContent,
  useFitment,
  useMyAccount
} from '../../shared/hooks/hooks';

import { getIsAppBasedDriver } from '../../shared/utils/treadwell/treadwell';

import {
  FuelTypeEnum,
  Maybe,
  ProductData,
  WeatherResult
} from '../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';

import styles from './lifespan-cost-warranty-info.scss';

interface LifespanCostWarrantyInfoProps {
  costPerMile?: number;
  median?: number;
  milesPerDollar?: number;
  minMileage?: number;
  months?: number;
  product?: ProductData;
  rollingResistanceRating?: number;
  totalCostOfOwnershipRating?: number;
  warranty?: number;
  weatherDetails?: Maybe<WeatherResult>;
  years?: number;
}

function LifespanCostWarrantyInfo(props: LifespanCostWarrantyInfoProps) {
  const {
    costPerMile,
    median,
    milesPerDollar,
    minMileage,
    months,
    product,
    rollingResistanceRating: rollingResistanceRatingProp,
    totalCostOfOwnershipRating,
    warranty,
    weatherDetails,
    years
  } = props;

  const { selectedFitment } = useFitment();
  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );

  const { isFleetAccount } = useMyAccount();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const { stopping } = product?.recommendationData || product?.tdsData || {};
  const { rollingResistanceRating: productRollingResistanceRating = 0 } =
    stopping || {};
  const rollingResistanceRating =
    rollingResistanceRatingProp || productRollingResistanceRating;

  const isAppBasedDriver = getIsAppBasedDriver();
  const hasAppBasedDriverData = Boolean(
    (isFleetTreadwell || isAppBasedDriver) &&
      (product?.recommendationData?.totalCost || costPerMile)
  );
  let isBatteryElectric = selectedFitment?.fuelType === FuelTypeEnum.Bev;
  const costPerMileContent = aemTooltipsContent?.[AEM_CONTENT_IDS.costPerMile];
  const evRangeContent = aemTooltipsContent?.[AEM_CONTENT_IDS.evRange];
  const evRangePendingContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.evRangePending];
  const fuelEfficiencyContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.fuelEfficiency];
  const fuelEfficiencyPendingContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.fuelEfficiencyPending];

  const isEfficiencyPending = rollingResistanceRating === 0;
  const efficiencyMaxLabel = isBatteryElectric ? 'longer range' : 'high MPG';
  const efficiencyMinLabel = isBatteryElectric ? 'shorter range' : 'low MPG';
  const efficiencyMidLabel = 'This tire has not been tested yet for efficiency';
  const efficiencyTitle = isBatteryElectric
    ? evRangeContent?.title
    : fuelEfficiencyContent?.title;
  const efficiencyPendingTitle = isBatteryElectric
    ? evRangePendingContent?.title
    : fuelEfficiencyPendingContent?.title;
  const efficiencyTooltip = isBatteryElectric
    ? evRangeContent?.tipText
    : fuelEfficiencyContent?.tipText;
  const efficiencyPendingTooltip = isBatteryElectric
    ? evRangePendingContent?.tipText
    : fuelEfficiencyPendingContent?.tipText;

  return (
    <div
      className={styles['container']}
      data-testid="lifespan-cost-warranty-info"
    >
      <div>
        {hasAppBasedDriverData ? (
          <>
            <CostPerMileInfo
              costPerMile={costPerMile}
              product={product}
              showTooltip
              title={costPerMileContent?.title}
              tooltip={costPerMileContent?.tipText}
              totalCostOfOwnershipRating={totalCostOfOwnershipRating}
            />
            <MilesDrivenAnnuallyInfo
              costPerMile={costPerMile}
              median={median}
              product={product}
              showTooltip
            />
          </>
        ) : (
          <TireLifeSpanInfo
            max={55}
            min={5}
            months={months}
            product={product}
            showTooltip
            winterDaysPerYear={weatherDetails?.winterDaysPerYear}
            years={years}
          />
        )}
      </div>

      <div>
        <TireLifeWarrantyInfo
          milesPerDollar={milesPerDollar}
          product={product}
          showTooltip
          warranty={warranty}
        />
        <TireLifeEstimateInfo
          median={median}
          minMileage={minMileage}
          product={product}
          showTooltip
        />
        <div data-testid="lifespan-cost-warranty__efficiency-info">
          <TreadwellBarRow
            barColor={
              isEfficiencyPending
                ? TreadwellBarRowColor.GREY
                : TreadwellBarRowColor.GREEN
            }
            className={styles['border-row']}
            icon={() => (
              <img
                alt="Efficiency Logo"
                className={styles['efficiency-icon']}
                height="20"
                src="https://discounttire.scene7.com/is/content/discounttire/logos/icons/treadwell-blue-tire-leaf.svg"
                width="20"
              />
            )}
            maxRatingLabel={
              !isEfficiencyPending ? efficiencyMaxLabel : undefined
            }
            midRatingLabel={
              isEfficiencyPending ? efficiencyMidLabel : undefined
            }
            minRatingLabel={
              !isEfficiencyPending ? efficiencyMinLabel : undefined
            }
            title={
              isEfficiencyPending ? efficiencyPendingTitle : efficiencyTitle
            }
            tooltip={
              isEfficiencyPending ? efficiencyPendingTooltip : efficiencyTooltip
            }
            value={rollingResistanceRating}
          />
        </div>
      </div>
    </div>
  );
}

export default LifespanCostWarrantyInfo;
